import React, { useEffect, useMemo, useState } from 'react';
import { Card, Stack, Tab, Tabs } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import ListInventory from '@screens/BranchOffices/Inventory/ListInventory';
import ListInventoryOrders from '@screens/BranchOffices/Inventory/ListInventoryOrders';
import ListAgotados from '@screens/BranchOffices/Inventory/ListAgotados';
import ListInventoryMovements from '@screens/BranchOffices/Inventory/ListInventoryMovements';
import BranchOfficesService from '@api/BranchOffices/BranchOfficeCtrl';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';

const InventoryScreen: React.FunctionComponent = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const navigation = useNavigate();
    const location = useLocation();
    const branchOfficesService = useMemo(() => new BranchOfficesService(), []);
    const [currentBranchOffice, setCurrentBranchOffice] = useState<BranchOffice | undefined>(undefined);
    const [activeTab, setActiveTab] = useState('inventory');

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
    };

    const onNewOrder = () => {
        if (currentBranchOffice?.id && currentBranchOffice.distributionCenterId) {
            navigation('pos', {
                state: {
                    branchOfficeId: currentBranchOffice.id,
                    distributionCenterId: currentBranchOffice.distributionCenterId
                }
            });
        }
    };

    const getData = async (branchOfficeId: string) => {
        showLoader();
        try {
            const dataBranchOffice = await branchOfficesService.getBranchOffice(branchOfficeId);
            setCurrentBranchOffice(dataBranchOffice);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // La sucursal en sesión es la ÚNICA fuente: cambiarla en el sidebar recarga el
    // inventario. Antes este efecto dependía de `loggedUser`, así que cambiar de
    // sucursal dejaba en pantalla los datos de la anterior — parecía que todas las
    // sucursales tenían el mismo inventario. `location.state` se sigue aceptando
    // por compatibilidad, pero quien navega desde Sucursales ya fija la sucursal.
    useEffect(() => {
        const state = location.state as { branchOfficeId?: string } | undefined;
        const branchOfficeId = branchOfficeSelected?.id || state?.branchOfficeId;
        if (branchOfficeId) {
            void getData(branchOfficeId);
        }
        // eslint-disable-next-line
    }, [branchOfficeSelected?.id, location.state]);

    return (
        <Stack height="100%" spacing={2} p={2}>
            <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Inventario" value="inventory" />
                <Tab label="Movimientos" value="movements" />
                <Tab label="Pedidos" value="orders" />
                <Tab label="Agotados" value="agotados" />
            </Tabs>
            <Stack component={Card} flex={1}>
                {activeTab === 'inventory' && currentBranchOffice?.id && (
                    <ListInventory
                        branchOfficeId={currentBranchOffice.id}
                        stockDays={currentBranchOffice.stockDays}
                        onNewOrder={onNewOrder}
                    />
                )}
                {/* Historial del ledger (F5 ②): la sucursal solo tenía saldo,
                    sin explicación de cómo llegó ahí */}
                {activeTab === 'movements' && currentBranchOffice?.id && (
                    <ListInventoryMovements branchOfficeId={currentBranchOffice.id} />
                )}
                {activeTab === 'orders' && currentBranchOffice?.id && (
                    <ListInventoryOrders branchOfficeId={currentBranchOffice.id} />
                )}
                {activeTab === 'agotados' && currentBranchOffice?.id && (
                    <ListAgotados branchOfficeId={currentBranchOffice.id} />
                )}
            </Stack>
        </Stack>
    );
};
export default InventoryScreen;
